// src/features/auth/ScopeProvider.jsx
import React from 'react'
import { auth, db } from '@/firebase'
import { doc, onSnapshot } from 'firebase/firestore'

/**
 * Access Context
 * Holds the signed-in user and their Firestore profile (users/{emailLower})
 */
const AccessCtx = React.createContext({
  loading: true,
  error: '',
  user: null,
  profile: null,
})

const lc = (s) => (s || '').toString().trim().toLowerCase()

function syncSessionFromProfile(user, profile) {
  if (!user?.email || !profile) return
  try {
    const current = JSON.parse(localStorage.getItem('session') || 'null') || {}
    const next = {
      ...current,
      email: lc(profile?.email || user.email),
      displayName: profile?.displayName || current.displayName || '',
      title: profile?.title || '',
      adminLevel: profile?.adminLevel || 'none',
      roles: Array.isArray(profile?.roles) ? profile.roles : [],
      communities: Array.isArray(profile?.communities) ? profile.communities : [],
      allCommunities: profile?.allCommunities === true,
      requirePasswordChange: !!profile?.requirePasswordChange,
    }
    localStorage.setItem('session', JSON.stringify(next))
    window.dispatchEvent(new Event('sessionchange'))
  } catch (e) {
    console.warn('[ScopeProvider] session sync failed', e)
  }
}

export function useAccess() {
  return React.useContext(AccessCtx)
}

/**
 * ScopeProvider
 * Watches Firebase Auth and syncs the user's Firestore profile.
 * Replaces the old memberships logic.
 */
export function ScopeProvider({ children }) {
  const [state, setState] = React.useState({
    loading: true,
    error: '',
    user: null,
    profile: null,
  })

  React.useEffect(() => {
    let offDoc=()=>{},generation=0
    const clearSession=()=>{localStorage.removeItem('session');window.dispatchEvent(new Event('sessionchange'))}
    // Track auth state
    const offAuth = auth.onAuthStateChanged((u) => {
      offDoc();offDoc=()=>{};const currentGeneration=++generation
      setState({loading:true,error:'',user:u,profile:null})
      if (!u) {
        clearSession()
        setState({ loading: false, error: '', user: null, profile: null })
        return
      }

      const id = (u.email || '').trim().toLowerCase()
      if (!id) {
        clearSession()
        setState({ loading: false, error: 'missing_email', user: u, profile: null })
        return
      }

      // Stream Firestore user doc
      offDoc = onSnapshot(
        doc(db, 'users', id),
        (snap) => {
          if(currentGeneration!==generation)return
          const profile = snap.exists()
            ? { id: snap.id, ...snap.data() }
            : null
          if(!profile||profile.isActive!==true||profile.status==='archived'){clearSession();setState({loading:false,error:'Account missing or inactive',user:u,profile:null});return}
          syncSessionFromProfile(u, profile)
          setState({ loading: false, error: '', user: u, profile })
        },
        (err) => {
          if(currentGeneration!==generation)return
          clearSession()
          console.error('[ScopeProvider] profile load failed', err)
          setState({ loading: false, error: err.message, user: u, profile: null })
        }
      )


    })

    // Cleanup
    return () => {
      generation++;offDoc()
      try { offAuth() } catch {}
    }
  }, [])

  return (
    <AccessCtx.Provider value={state}>
      {children}
    </AccessCtx.Provider>
  )
}
